import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { assistantAvailabilityKey } from '@/features/assistant/useAssistantAvailability';
import {
  AI_PROVIDERS,
  aiAssistantSettingsService,
  type AiModelOption,
  type AiProvider,
  type UpdateAiAssistantSettings,
} from '@/services/aiAssistantSettingsService';

export const aiAssistantSettingsKeys = {
  all: ['aiAssistantSettings'] as const,
  models: (provider: AiProvider) => ['aiAssistantSettings', 'models', provider] as const,
  usage: (from: string, to: string) => ['aiAssistantSettings', 'usage', from, to] as const,
};

export function useAiAssistantSettings() {
  return useQuery({
    queryKey: aiAssistantSettingsKeys.all,
    queryFn: () => aiAssistantSettingsService.get(),
  });
}

/** The provider's catalog; a provider without one answers with an empty list. */
export function useAiModels(provider: AiProvider) {
  return useQuery({
    queryKey: aiAssistantSettingsKeys.models(provider),
    queryFn: () => aiAssistantSettingsService.getModels(provider),
    staleTime: 10 * 60 * 1000,
  });
}

/** Every provider's catalog, from the same cache as useAiModels; a provider not read yet has an empty list. */
export function useAiModelCatalogs(): Record<AiProvider, AiModelOption[]> {
  return useQueries({
    queries: AI_PROVIDERS.map((provider) => ({
      queryKey: aiAssistantSettingsKeys.models(provider),
      queryFn: () => aiAssistantSettingsService.getModels(provider),
      staleTime: 10 * 60 * 1000,
    })),
    combine: (results) => Object.fromEntries(
      AI_PROVIDERS.map((provider, i) => [provider, results[i].data ?? []]),
    ) as Record<AiProvider, AiModelOption[]>,
  });
}

/** Usage between two dates (YYYY-MM-DD, both inclusive); not asked for until both are given. */
export function useAiUsage(from: string, to: string) {
  return useQuery({
    queryKey: aiAssistantSettingsKeys.usage(from, to),
    queryFn: () => aiAssistantSettingsService.getUsage(from, to),
    enabled: from !== '' && to !== '',
  });
}

export function useUpdateAiAssistantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAiAssistantSettings) => aiAssistantSettingsService.update(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(aiAssistantSettingsKeys.all, settings);
      // The saver's own roles may have gained or lost the assistant.
      void queryClient.invalidateQueries({ queryKey: assistantAvailabilityKey });
    },
  });
}

export function useTestAiConnection() {
  return useMutation({ mutationFn: () => aiAssistantSettingsService.testConnection() });
}
